/* Tarifs par défaut des contrats (CRM → Paramètres → Tarifs par défaut), en c€/kWh HTVA.
 * Un tarif par type de personne (particulier / entreprise) et par sens (consommation / production).
 * Consommateur : prix d'achat = tarif consommation. Producteur : prix = tarif production.
 * Prosumer : prix d'achat = tarif consommation, prix de vente = tarif production.
 * Stockés dans crm_settings (clé « default_prices ») ; la base applique les mêmes valeurs aux leads créés sans tarif. */
import { supabase } from '../../lib/supabase';
import type { CrmContactDraft, EntityType, Role } from './types';

export interface PriceSet { consumer: number | null; producer: number | null }
export type DefaultPrices = Record<EntityType, PriceSet>;

export const EMPTY_DEFAULT_PRICES: DefaultPrices = {
  physique: { consumer: null, producer: null },
  morale: { consumer: null, producer: null },
};

const KEY = 'default_prices';
let cache: DefaultPrices | null = null;

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export function normalizeDefaultPrices(value: unknown): DefaultPrices {
  const v = (value ?? {}) as Partial<Record<EntityType, Partial<PriceSet>>>;
  return {
    physique: { consumer: num(v.physique?.consumer), producer: num(v.physique?.producer) },
    morale: { consumer: num(v.morale?.consumer), producer: num(v.morale?.producer) },
  };
}

export async function fetchDefaultPrices(force = false): Promise<DefaultPrices> {
  if (cache && !force) return cache;
  const { data, error } = await supabase.from('crm_settings').select('value').eq('key', KEY).maybeSingle();
  if (error) throw error;
  cache = normalizeDefaultPrices((data as { value?: unknown } | null)?.value);
  return cache;
}

export async function saveDefaultPrices(value: DefaultPrices): Promise<DefaultPrices> {
  const clean = normalizeDefaultPrices(value);
  const { error } = await supabase.from('crm_settings')
    .upsert({ key: KEY, value: clean, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
  cache = clean;
  return clean;
}

export function defaultPriceErrors(value: DefaultPrices): Partial<Record<`${EntityType}.${keyof PriceSet}`, string>> {
  const errors: Partial<Record<`${EntityType}.${keyof PriceSet}`, string>> = {};
  (['physique', 'morale'] as EntityType[]).forEach(e => (['consumer', 'producer'] as (keyof PriceSet)[]).forEach(k => {
    const v = value[e][k];
    if (v !== null && (!(v > 0) || v >= 100)) errors[`${e}.${k}`] = 'Entre 0 et 100 c€/kWh';
  }));
  return errors;
}

/** Tarifs proposés pour un contact : `price` (prix du contrat / d'achat) et `sell` (prosumer uniquement). */
export function defaultPricesFor(
  prices: DefaultPrices | null, entity: EntityType | null | undefined, role: Role | null | undefined,
): { price: number | null; sell: number | null } {
  if (!prices || !role) return { price: null, sell: null };
  const set = prices[entity === 'morale' ? 'morale' : 'physique'];
  if (role === 'producer') return { price: set.producer, sell: null };
  if (role === 'prosumer') return { price: set.consumer, sell: set.producer };
  return { price: set.consumer, sell: null };
}

const empty = (v: unknown) => v === null || v === undefined || v === '';
const same = (a: unknown, b: number | null) => b !== null && !empty(a) && Number(a) === b;

/**
 * Changement de type de membre ou de personne : les tarifs suivent s'ils étaient vides
 * ou égaux au tarif par défaut précédent. Un tarif saisi à la main est conservé.
 */
export function pricesAfterChange(prices: DefaultPrices | null, prev: CrmContactDraft, next: CrmContactDraft): Partial<CrmContactDraft> {
  if (!prices) return {};
  const before = defaultPricesFor(prices, prev.entity_type, prev.role);
  const after = defaultPricesFor(prices, next.entity_type, next.role);
  const patch: Partial<CrmContactDraft> = {};
  if ((empty(prev.price_cents_kwh) || same(prev.price_cents_kwh, before.price)) && after.price !== null) patch.price_cents_kwh = after.price;
  if (next.role === 'prosumer') {
    if ((empty(prev.sell_price_cents_kwh) || same(prev.sell_price_cents_kwh, before.sell)) && after.sell !== null) patch.sell_price_cents_kwh = after.sell;
  }
  return patch;
}

/** Applique les tarifs par défaut aux leads (non archivés) qui n'ont pas encore de tarif. Renvoie le nombre de fiches modifiées. */
export async function applyDefaultPricesToLeads(prices: DefaultPrices): Promise<number> {
  const ids = new Set<string>();
  for (const entity of ['physique', 'morale'] as EntityType[]) {
    for (const role of ['consumer', 'producer', 'prosumer'] as Role[]) {
      const { price, sell } = defaultPricesFor(prices, entity, role);
      const updates: [string, number | null][] = [['price_cents_kwh', price], ...(role === 'prosumer' ? [['sell_price_cents_kwh', sell] as [string, number | null]] : [])];
      for (const [column, value] of updates) {
        if (value === null) continue;
        const { data, error } = await supabase.from('crm_contacts')
          .update({ [column]: value })
          .eq('stage', 'lead').eq('entity_type', entity).eq('role', role)
          .is(column, null).is('archived_at', null)
          .select('id');
        if (error) throw error;
        ((data as { id: string }[] | null) ?? []).forEach(r => ids.add(r.id));
      }
    }
  }
  return ids.size;
}
